/**
 * Contributor layout — focused MY WORK · OUTPUTS · WORKSPACE navigation
 * for non-admin users without elevated operational jobs.
 */

import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { ContributorSidebar, ContributorTopBar, contributorPageTitle } from './ContributorSidebar';
import { HarryShell } from '../harry/HarryShell';
import { HarryHeaderActions } from '../harry/HarryHeaderActions';
import { NotificationBell } from './NotificationBell';
import { monoIconButton } from '@/components/mono/iconButton';
import { C, NAV_H, SIDEBAR_W } from '../../features/sessions-participant/theme/participantTokens';
import { useUserStore } from '../../stores/prioritization/userStore';
import { clearTenantContext } from '../../lib/tenantContext';
import '../../styles/participant.css';

interface ContributorLayoutProps {
  children: ReactNode;
}

export function ContributorLayout({ children }: ContributorLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useUserStore((s) => s.signOut);
  const pageTitle = contributorPageTitle(location.pathname);

  return (
    <HarryShell>
    <div
      className="pp"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        background: C.bg,
        color: C.textPri,
      }}
    >
      <ContributorTopBar
        pageTitle={pageTitle}
        trailing={(
          /* No `dark` class here. It was added when this shell was a fixed dark
             canvas and the bell needed dark: variants; the shell now follows the
             theme and the controls are token-only, so the class only pinned the
             Night palette inside a light page (a navy box around each icon). */
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <NotificationBell />
            <HarryHeaderActions />
            <button
            type="button"
            onClick={() => {
              clearTenantContext();
              signOut();
              navigate('/auth/login', { replace: true });
            }}
            className={monoIconButton('sm')}
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4 shrink-0" />
          </button>
          </div>
        )}
      />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <ContributorSidebar
          style={{
            position: 'fixed',
            top: NAV_H,
            bottom: 0,
            left: 0,
          }}
        />

        <div style={{ marginLeft: SIDEBAR_W, flex: 1, minWidth: 0, padding: '20px 24px' }}>
          {children}
        </div>
      </div>
    </div>
    </HarryShell>
  );
}
